import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Button from "common/components/Elements/Button/Button";
import Denotation from "common/components/Elements/Denotation/Denotation";
import { hasContentForMessages, type MessagesProps } from "common/components/Elements/ExpandableMessages/utils.ts";
import Icon from "common/components/Elements/Icon/Icon";
import SanitizeHTML from "common/components/Elements/SanitizeHTML/SanitizeHTML";
import { arrayLast } from "common/utilities/core/array";
import { generateRandomString } from "common/utilities/core/string";
import * as I from "core/api/coreApiClient";
import theme, { ThemeColors } from "core/utilities/theme";
import I18n from "i18n/en-US";
import React from "react";

export interface ComponentProps {
  messages: I.Message[];
  maxShown: number;
}

const groupedMessages = (messages: I.Message[], maxShown?: number) =>
  messages.slice(0, maxShown).reduce<I.Message[][]>((acc, message) => {
    if (!acc[0]) return [[message]];
    const lastGroup = arrayLast(acc);

    if (message.severity === lastGroup[0].severity) {
      lastGroup.push(message);
      return [...acc.slice(0, acc.length - 1), lastGroup];
    }

    return [...acc, [message]];
  }, []);

const severityColor = (severity: I.SeverityConstants) => {
  let color = theme.palette.primary.main;
  if (severity === I.SeverityConstants.WARN) {
    color = ThemeColors.softWarning;
  }
  if (severity === I.SeverityConstants.ERROR) {
    color = ThemeColors.deleteWarning;
  }
  return color;
};

function SeverityIcon({ severity }: { severity: I.SeverityConstants }) {
  return (
    <>
      {severity !== I.SeverityConstants.WARN && severity !== I.SeverityConstants.ERROR && <Icon element={InfoIcon} />}
      {severity === I.SeverityConstants.WARN && <Icon element={WarningAmberIcon} />}
      {severity === I.SeverityConstants.ERROR && <Icon element={CancelIcon} />}
    </>
  );
}

export function Messages({ messages, denotation }: MessagesProps) {
  if (!hasContentForMessages(messages, denotation)) return null;

  return (
    <>
      {(messages || []).map((message) => (
        <span key={generateRandomString()} data-e2e-label="notification-content">
          <SanitizeHTML html={message.text} disallowedTagsMode="escape" />
        </span>
      ))}
      {denotation ? (
        <span style={{ display: "inline-block", fontSize: 12, width: "100%" }}>
          <Denotation denotation={denotation} />
        </span>
      ) : null}
    </>
  );
}

type SeverityCount = Partial<Record<I.SeverityConstants, number>>;

function ExpandableMessages({ messages, maxShown }: ComponentProps) {
  const [showAll, setShowAll] = React.useState(false);

  if (messages.length === 0) return null;

  const severityCountMissing = messages.slice(maxShown).reduce<SeverityCount>((acc, message) => {
    const newAcc: SeverityCount = {
      ...acc,
      [message.severity]: (acc[message.severity] || 0) + 1,
    };
    return newAcc;
  }, {});

  return (
    <Box style={{ maxWidth: "50vw" }} data-e2e-notification="">
      {messages.length > maxShown && !showAll ? (
        <>
          <Box>
            {groupedMessages(messages, maxShown).map((messagesFromGroup) => (
              <Messages key={generateRandomString()} messages={messagesFromGroup} />
            ))}
          </Box>
          <Box ml="20px">
            <Box display="inline-block" ml={-0.5}>
              <IconButton onClick={() => setShowAll(true)} size="large">
                <Icon element={AddIcon} />
              </IconButton>
            </Box>
            {Object.keys(severityCountMissing).map((severity) => (
              <Box key={`severity-${severity}`} display="inline-block" mr={1}>
                <Chip
                  size="small"
                  icon={
                    <Box style={{ color: severityColor(severity as I.SeverityConstants) }} sx={{ mt: 0.5 }}>
                      <SeverityIcon severity={severity as I.SeverityConstants} />
                    </Box>
                  }
                  label={I18n.Elements.ExpandableMessages[severity as I.SeverityConstants](
                    severityCountMissing[severity as I.SeverityConstants] as number,
                  )}
                  variant="outlined"
                  onClick={() => setShowAll(true)}
                  data-e2e-notification-expand=""
                />
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <>
          <Box>
            {groupedMessages(messages).map((messagesFromGroup) => (
              <Messages key={generateRandomString()} messages={messagesFromGroup} />
            ))}
          </Box>
          {messages.length > maxShown && (
            <Box ml="20px">
              <Button
                action="tertiary"
                onClick={() => setShowAll(false)}
                small
                inheritColor
                data-e2e-notification-collapse=""
              >
                <Icon element={RemoveIcon} />
                {I18n.Elements.ExpandableMessages.less}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

/** Simple notification shown within a page content (not via Notifier) */
export function NotificationMessage({ messages }: { messages: I.Message[] }) {
  return (
    <Box
      p={2}
      mb={1}
      style={{
        backgroundColor: ThemeColors.notificationBg,
        borderRadius: "4px",
      }}
    >
      <ExpandableMessages messages={messages} maxShown={messages.length} />
    </Box>
  );
}

export default ExpandableMessages;
